import { useState } from "react";
import { ImageOff, Maximize2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Markdown } from "@/components/Markdown";
import { PictureLightbox } from "@/components/PictureLightbox";

// EP-19 — a question's prompt plus its optional picture. The picture is a
// STRUCTURED field (never markdown), rendered under the prompt text on every
// surface that shows a question. Its `src` is built by the CONTAINER, because
// the route differs by viewer and both are our own origin:
//   * a candidate — by POSITION, bound to their token + attempt
//     (`candidatePictureSrc`), so no key or company id ever reaches them;
//   * a recruiter — by name, under their own company (`adminPictureSrc`).
// `width`/`height` are the stored size, so the box is reserved before the
// bytes arrive (no layout jump under the answer options).

export interface QuestionPictureInfo {
  src: string;
  alt: string;
  width: number;
  height: number;
}

/** The wire shape a question snapshot carries (`QuestionImagePublic`). */
export interface QuestionImageRef {
  sha256: string;
  ext: string;
  width: number;
  height: number;
  alt?: string;
}

export function adminPictureSrc(image: { sha256: string; ext: string }): string {
  return `/api/admin/question-images/${image.sha256}.${image.ext}`;
}

export function candidatePictureSrc(
  token: string,
  position: number,
  attemptId: string,
  image: { sha256: string },
): string {
  const q = new URLSearchParams({ attempt_id: attemptId, v: image.sha256.slice(0, 16) });
  return `/api/t/${encodeURIComponent(token)}/question/${position}/image?${q.toString()}`;
}

/**
 * A blank stand-in with the picture's own intrinsic size: the box is reserved
 * (no jump under the answer options) and no request leaves. The candidate page
 * shows it while the real one may not be asked for yet — `awaitsClock`.
 */
export function reservedPictureSrc(image: { width: number; height: number }): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${image.width}" height="${image.height}"/>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** A snapshot's picture → what `QuestionPicture` renders, or null. */
export function pictureFrom(
  image: QuestionImageRef | null | undefined,
  src: (image: QuestionImageRef) => string,
): QuestionPictureInfo | null {
  if (!image) return null;
  return {
    src: src(image),
    alt: image.alt ?? "",
    width: image.width,
    height: image.height,
  };
}

export function QuestionPicture({ picture }: { picture: QuestionPictureInfo }) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  if (failed) {
    // Never a broken-image icon mid-assessment: say what is missing, calmly.
    return (
      <p
        role="note"
        className="mt-3 flex items-start gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
      >
        <ImageOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          The picture could not be loaded
          {picture.alt ? <> — it shows: {picture.alt}</> : null}.
        </span>
      </p>
    );
  }

  return (
    <figure className="mt-3">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge the picture${picture.alt ? `: ${picture.alt}` : ""}`}
        className="group relative block max-w-full cursor-zoom-in overflow-hidden rounded-md border border-border bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <img
          src={picture.src}
          alt={picture.alt}
          width={picture.width}
          height={picture.height}
          decoding="async"
          draggable={false}
          onError={() => setFailed(true)}
          className="block h-auto max-h-[28rem] w-auto max-w-full object-contain"
        />
        <span
          aria-hidden="true"
          className="absolute bottom-1.5 right-1.5 rounded bg-background/90 p-1 text-foreground opacity-80 transition-opacity group-hover:opacity-100"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </span>
      </button>
      {open && (
        <PictureLightbox
          src={picture.src}
          alt={picture.alt}
          title={picture.alt || "Picture"}
          onClose={() => setOpen(false)}
        />
      )}
    </figure>
  );
}

export function QuestionPrompt({
  promptMd,
  picture,
  codeLanguage,
}: {
  promptMd: string;
  picture?: QuestionPictureInfo | null;
  /** FEAT-17: a code question's language — colours an untagged code block. */
  codeLanguage?: string;
}) {
  return (
    <>
      <Markdown source={promptMd} codeLanguage={codeLanguage} />
      {/* night-p5: keyed on the src. `QuestionPicture` keeps `failed` (and
          the open lightbox) in local state, and this prompt re-renders IN
          PLACE when its picture changes — a recruiter replacing a picture on
          the EditTest card after the old one failed to load kept reading
          "could not be loaded", and an open lightbox would silently show the
          new file under the old title. A new src is a new picture. (The
          candidate flow happens to remount per route — the boundary in
          App.tsx is keyed on the pathname — so it never showed this.) */}
      {picture ? <QuestionPicture key={picture.src} picture={picture} /> : null}
    </>
  );
}

/**
 * The candidate-facing prompt card — the frame around `QuestionPrompt` that
 * every answer component draws.
 *
 * ONE frame for all four (MCQ, MCQMulti, OpenEnded, FileUploadAnswer). It was
 * hand-repeated in each of them, which put the page's ONLY `h1` in four places
 * at once: a heading level that drifted on one answer type would leave that
 * screen with no top-level heading and no visible symptom (SIMP-6). The module
 * that owns the prompt owns its frame.
 */
export function QuestionPromptCard({
  promptMd,
  picture,
}: {
  promptMd: string;
  picture?: QuestionPictureInfo | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">Question</CardTitle>
      </CardHeader>
      <CardContent>
        <QuestionPrompt promptMd={promptMd} picture={picture} />
      </CardContent>
    </Card>
  );
}

export default QuestionPrompt;
